import { inject, Injectable, signal } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AccessToken, OktaAuth, Tokens, UserClaims } from '@okta/okta-auth-js';
import { Router } from '@angular/router';
import { environment } from '@brighthub/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _router = inject(Router);
  private _userPromise: Promise<UserClaims> | null = null;


  readonly _env = environment;
  readonly _oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  readonly loggedIn = signal(false);
  readonly userDisplayName = signal('');
  readonly user = signal<UserClaims | null>(null);

  constructor() {
    this._oktaAuth.authStateManager.subscribe(async () => {
      const auth = await this._oktaAuth.isAuthenticated();
      this.loggedIn.set(auth);
    });

    this._oktaAuth.isAuthenticated().then((auth) => {
      if (auth) {
        this.getUser().then(user => {
          this.userDisplayName.set(`${user.given_name} ${user.family_name}`);
        });
      }
     this.loggedIn.set(auth);
    });
  }

  async isAuthenticated(): Promise<boolean> {
    return this._oktaAuth.isAuthenticated();
  }

  async getUser(): Promise<UserClaims> {
    const cached = this.user();
    if (cached) return cached;

    // if there's already a request in progress, return that
    if (this._userPromise) return this._userPromise;

    // otherwise fetch and cache
    this._userPromise = this._oktaAuth.getUser().then(user => {
      this.user.set(user);
      this._userPromise = null; // clear promise after it resolves
      return user;
    }).catch(err => {
      this._userPromise = null;
      throw err;
    });

    return this._userPromise;
  }

  getDecodedIdToken(): Record<string, any> | null {
    const token = this.getIdToken();
    return token ? this._decode(token) : null;
  }

  getDecodedAccessToken(): Record<string, any> | null {
    const token = this._oktaAuth.getAccessToken();
    return token ? this._decode(token) : null;
  }

  getUserGroups(): string[] {
    return this.getDecodedAccessToken()?.['groups'] ?? [];
  }

  isInGroup(group: string | string[]): boolean {
    const userGroups = this.getUserGroups();
    if (Array.isArray(group)) {
      return group.some(g => userGroups.includes(g));
    }
    return userGroups.includes(group);
  }

  getIdToken(): string | undefined {
    return this._oktaAuth.getIdToken();
  }

  isAccessToken(token: unknown): token is AccessToken {
    return (
      !!token &&
      typeof token === 'object' &&
      'accessToken' in token &&
      typeof (token as any).accessToken === 'string'
    );
  }

  async getAccessToken(): Promise<string | undefined> {
    const token = await this._oktaAuth.tokenManager.get('accessToken');
    if (this.isAccessToken(token)) {
      const currentTime = Math.floor(Date.now() / 1000);
      if (token.expiresAt - currentTime < 60) {
        // About to expire – proactively refresh
        return await this.refreshAccessToken();
      }
      return token.accessToken;
    }
    return undefined;
  }

  getTokens(): Tokens {
    return this._oktaAuth.tokenManager.getTokensSync();
  }

  async refreshAccessToken(): Promise<string | undefined> {
    try {
      const freshToken = await this._oktaAuth.tokenManager.renew('accessToken');
      if (this.isAccessToken(freshToken)) {
        return freshToken.accessToken;
      }
    } catch (err) {
      console.error('Failed to refresh access token');
      return undefined;
    }
    return undefined;
  }


  signIn(originalUri?: string): Promise<void> {
    return this._oktaAuth.signInWithRedirect({ originalUri });
  }

  async signOut() {
    await this._oktaAuth.tokenManager.clear();
    this.user.set(null);
    this._userPromise = null; // reset cached request
    this.loggedIn.set(false);
    await this._router.navigateByUrl('/login/okta');
  }

  async refresh(): Promise<void> {
    await this._oktaAuth.authStateManager.updateAuthState();
  }

  private _decode(token: string): Record<string, any> {
    return JSON.parse(atob(token.split('.')[1]));
  }
}
